import React from "react";
import {
  Box,
  ButtonBase,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Outlet } from "react-router-dom";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function MobileProjectSettingsLayout({
  sections,
  section,
  onSectionChange,
  project,
  activeModules,
  billingLabel,
  billingColor,
  guidedAreas = [],
  onAreaOpen,
}) {
  const active = sections.find((item) => item.value === section) || sections[0];

  return (
    <Stack spacing={1.25} sx={{ pb: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 950, letterSpacing: .7 }}>
            CONFIGURACIÓN
          </Typography>
          <Typography variant="h4" noWrap sx={{ color: BRAND.dark, fontWeight: 950 }}>
            {active?.label || "Negocio"}
          </Typography>
        </Box>
        <Chip
          size="small"
          color={billingColor}
          variant="outlined"
          label={billingLabel}
          sx={{ maxWidth: 128, fontWeight: 900, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
        />
      </Stack>

      {guidedAreas.length ? (
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 3, border: "1px solid rgba(25,36,47,.08)" }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: 13.5 }}>
            Cierre y mantenimiento
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: .2, mb: .9, color: "rgba(25,36,47,.56)" }}>
            Priorizá lo pendiente sin mezclar tareas operativas con Configuración.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: .65 }}>
            {guidedAreas.map((area) => (
              <ButtonBase
                key={area.key}
                onClick={() => onAreaOpen?.(area)}
                aria-label={`Abrir ${area.title}`}
                sx={{
                  minWidth: 0,
                  alignItems: "stretch",
                  justifyContent: "stretch",
                  textAlign: "left",
                  borderRadius: 2.2,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    p: .8,
                    borderRadius: 2.2,
                    border: "1px solid rgba(25,36,47,.08)",
                    bgcolor: area.status?.tone === "warning" ? "rgba(237,108,2,.035)" : "#fff",
                  }}
                >
                  <Stack direction="row" spacing={.6} alignItems="center">
                    <Box sx={{ display: "grid", placeItems: "center", color: area.status?.tone === "warning" ? "warning.main" : BRAND.orange }}>
                      {area.icon}
                    </Box>
                    <Typography noWrap sx={{ minWidth: 0, flex: 1, color: BRAND.dark, fontSize: 10.5, fontWeight: 950 }}>
                      {area.title}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: .35,
                      color: area.status?.tone === "warning" ? "warning.dark" : "rgba(25,36,47,.52)",
                      fontSize: 9.5,
                      fontWeight: 850,
                    }}
                  >
                    {area.status?.label}
                  </Typography>
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Paper>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: .65,
          borderRadius: 3,
          border: "1px solid rgba(25,36,47,.08)",
          display: "grid",
          gridTemplateColumns: "repeat(5,minmax(0,1fr))",
          gap: .45,
          overflow: "hidden",
        }}
      >
        {sections.map((item) => {
          const selected = item.value === section;
          return (
            <ButtonBase
              key={item.value}
              onClick={() => onSectionChange(item.value)}
              aria-label={item.label}
              sx={{
                minWidth: 0,
                borderRadius: 2.2,
                py: .8,
                px: .25,
                display: "flex",
                flexDirection: "column",
                gap: .25,
                color: selected ? "#fff" : "rgba(25,36,47,.62)",
                bgcolor: selected ? BRAND.orange : "transparent",
              }}
            >
              {React.cloneElement(item.icon, { sx: { fontSize: 21 } })}
              <Typography noWrap sx={{ width: "100%", fontSize: 9.5, fontWeight: 900, px: .1 }}>
                {item.mobileLabel || item.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid rgba(25,36,47,.08)",
          bgcolor: "#FBFBFD",
          p: 1.15,
          overflow: "hidden",
          "& .MuiCard-root, & .MuiPaper-root": { borderRadius: "12px" },
          "& .MuiCardContent-root": { p: "12px !important" },
          "& .MuiFormHelperText-root": { mt: .45, fontSize: ".67rem", lineHeight: 1.25 },
          "& .MuiTypography-body1": { fontSize: ".86rem", lineHeight: 1.42 },
          "& .MuiTypography-body2": { fontSize: ".78rem", lineHeight: 1.35 },
          "& .MuiButton-root": { minHeight: 40, borderRadius: 2.2, textTransform: "none", fontWeight: 900 },
          "& .MuiTextField-root": { my: .15 },
          "& .MuiInputBase-root": { fontSize: ".86rem" },
          "& .MuiGrid-container": { mt: "-8px", width: "calc(100% + 8px)", ml: "-8px" },
          "& .MuiGrid-item": { pt: "8px", pl: "8px" },
        }}
      >
        <Outlet />
      </Paper>

      <Paper elevation={0} sx={{ p: 1.2, borderRadius: 3, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ color: BRAND.dark, fontWeight: 950, fontSize: 13.5 }}>
              {project?.name || "Proyecto"}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>
              {activeModules} módulos activos
            </Typography>
          </Box>
          <ChevronRightRoundedIcon sx={{ color: "rgba(25,36,47,.35)" }} />
        </Stack>
      </Paper>
    </Stack>
  );
}
